/**
 * CRM context — exposes the active adapter to the React tree.
 *
 * The adapter is created lazily from tenant.config.json. If the tenant hasn't
 * connected a CRM yet, `adapter` is null and the Dashboard should render a
 * "Connect CRM" prompt linking into the Setup Wizard.
 *
 * `connectionStatus` is a soft probe — we ping the adapter with a low-cost call
 * (`listOwners`) once on mount, so the dashboard can show "Connecting…" → "Live"
 * without every individual hook racing the auth state.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { getAdapterForTenant, getCrmAdapter } from '../lib/crm';
import { useTenant } from './TenantContext';
import type { CrmAdapter } from '../lib/crm/adapter';
import type { CrmType } from '../lib/crm/types';
import { AdapterError } from '../lib/crm/adapter';

export type CrmConnectionStatus = 'unconfigured' | 'connecting' | 'live' | 'error';

interface CrmContextValue {
  adapter: CrmAdapter | null;
  crmType: CrmType | null;
  status: CrmConnectionStatus;
  /** Last error from the probe call, if any. */
  error: AdapterError | null;
  /** Re-run the probe — useful after the user completes the install flow. */
  reprobe: () => void;
}

const CrmContext = createContext<CrmContextValue | undefined>(undefined);

export function CrmProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  // Demo tenants get a canned-data adapter so the live demo dashboards show
  // a populated map + pipeline without any real CRM connection. Real tenants
  // continue to use the per-fork static tenant.config.json adapter.
  const adapter = useMemo(() => {
    if (tenant?.id) {
      const isDemo = (tenant as { is_demo?: boolean }).is_demo === true;
      return getAdapterForTenant({ isDemo, slug: tenant.slug });
    }
    return getCrmAdapter();
  }, [tenant?.id, tenant?.slug]);
  const [status, setStatus] = useState<CrmConnectionStatus>(
    adapter ? 'connecting' : 'unconfigured',
  );
  const [error, setError] = useState<AdapterError | null>(null);
  const [probeTick, setProbeTick] = useState(0);

  useEffect(() => {
    if (!adapter) {
      setStatus('unconfigured');
      return;
    }
    let cancelled = false;
    setStatus('connecting');
    setError(null);
    adapter
      .listOwners()
      .then(() => {
        if (!cancelled) setStatus('live');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const adapterErr =
          err instanceof AdapterError
            ? err
            : new AdapterError('UNKNOWN', err instanceof Error ? err.message : String(err));
        setError(adapterErr);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, probeTick]);

  const value: CrmContextValue = {
    adapter,
    crmType: adapter?.type ?? null,
    status,
    error,
    reprobe: () => setProbeTick((n) => n + 1),
  };

  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
}

export function useCrm(): CrmContextValue {
  const ctx = useContext(CrmContext);
  if (!ctx) throw new Error('useCrm must be used within CrmProvider');
  return ctx;
}

/** Convenience for callers that only care about the adapter and want to assert it's present. */
export function useCrmAdapter(): CrmAdapter {
  const { adapter } = useCrm();
  if (!adapter) {
    throw new Error(
      '[useCrmAdapter] No CRM connected. Guard with useCrm().status !== "unconfigured".',
    );
  }
  return adapter;
}
